/**
 * Qué ve cada rol.
 *
 * Hay dos roles: **supervisor** (el usuario marcado como administrador) y
 * **cajero** (el resto). El supervisor ve todo; lo que se declara acá es qué
 * alcanza el cajero.
 *
 * Antes esto vivía repartido en tres sitios que podían contradecirse: una lista
 * de claves de menú escrita a mano, un conjunto de rutas bloqueadas escrito
 * aparte, y un `adminOnly` por ruta en el enrutador. Nada garantizaba que las
 * tres dijeran lo mismo -- y de hecho la lista de menú y la de rutas cubrían
 * pantallas distintas. Ahora las tres se derivan de esta tabla.
 *
 * Mismo criterio que `actions.config.js` y que `endpointConfig`: se declara una
 * vez y los consumidores se derivan.
 */

// Una pantalla que no declara `cajero` no la ve el cajero. El silencio niega,
// no concede: si mañana se agrega una pantalla y nadie decide su rol, lo seguro
// es que no aparezca, no que aparezca para todos.
const PANTALLAS = {
  inicio: { nombre: "Inicio", grupo: "General", menu: "1", ruta: "/", cajero: true },

  clientes: { nombre: "Clientes", grupo: "Maestros", menu: "2.1", ruta: "/clientes" },
  productos: {
    nombre: "Productos",
    grupo: "Maestros",
    menu: "2.2",
    ruta: "/productos",
    cajero: true,
  },
  formasDePago: {
    nombre: "Formas de pago",
    grupo: "Maestros",
    menu: "2.3",
    ruta: "/formas-pago",
  },
  colegios: { nombre: "Colegios", grupo: "Maestros", menu: "2.4", ruta: "/colegios" },
  tiendas: { nombre: "Tiendas", grupo: "Maestros", menu: "2.5", ruta: "/tpvs" },
  preciosDeVenta: {
    nombre: "Precios de venta",
    grupo: "Maestros",
    menu: "2.6",
    ruta: "/precios-venta",
  },
  cupones: { nombre: "Cupones activos", grupo: "Maestros", menu: "2.7", ruta: "/cupones" },
  campanas: { nombre: "Campañas", grupo: "Maestros", menu: "2.8", ruta: "/campanas" },
  vendedores: { nombre: "Vendedores", grupo: "Maestros", menu: "2.9", ruta: "/vendedores" },

  consultaDeStock: {
    nombre: "Consulta de stock",
    grupo: "General",
    menu: "3",
    ruta: "/consulta-stock",
    cajero: true,
  },

  turnos: { nombre: "Turnos", grupo: "Maestros", menu: "2.11", ruta: "/turnos" },
  cajas: { nombre: "Cajas", grupo: "Maestros", menu: "2.10", ruta: "/cajas" },

  // Aperturas y cierres los ve el cajero, pero **sólo para consultar**: son su
  // propio trabajo y necesita revisarlo, no rehacerlo. Editar, eliminar y
  // reenviar a Business Central quedan para el supervisor.
  aperturasDeCaja: {
    nombre: "Aperturas de caja",
    grupo: "Gestión",
    menu: "5.1",
    ruta: "/aperturas-caja",
    cajero: true,
    soloLecturaCajero: true,
  },
  cierresDeCaja: {
    nombre: "Cierres de caja",
    grupo: "Gestión",
    menu: "5.2",
    ruta: "/cierres-caja",
    cajero: true,
    soloLecturaCajero: true,
  },
  // El cajero la ve: es su propio turno, y consultarlo antes de entregarlo o
  // de cerrar es parte de su trabajo. No escribe nada, así que no hay un
  // "sólo lectura" que declarar -- la pantalla entera lo es.
  comoVaElTurno: {
    nombre: "Cómo va el turno",
    grupo: "Gestión",
    menu: "5.6",
    ruta: "/como-va-el-turno",
    cajero: true,
  },
  facturas: { nombre: "Facturas", grupo: "Gestión", menu: "5.3", ruta: "/facturas", cajero: true },
  abonos: { nombre: "Abonos", grupo: "Gestión", menu: "5.4", ruta: "/abonos", cajero: true },
  pendientesDeSincronizar: {
    nombre: "Pendientes de sincronizar",
    grupo: "Gestión",
    menu: "5.5",
    ruta: "/facturas-pendientes",
    cajero: true,
  },

  informes: { nombre: "Informes", grupo: "General", menu: "6", ruta: "/informes", cajero: true },
  usuarios: { nombre: "Usuarios", grupo: "Maestros", menu: "7", ruta: "/usuarios" },

  // Fuera del menú lateral: se llega por el menú de usuario del encabezado.
  miPerfil: { nombre: "Mi perfil", grupo: "Cuenta", ruta: "/perfil", cajero: true },
  configuracion: { nombre: "Configuración", grupo: "Cuenta", ruta: "/configuracion", cajero: true },
  notasDeLaVersion: {
    nombre: "Notas de la versión",
    grupo: "Cuenta",
    ruta: "/notas-version",
    cajero: true,
  },
};

/**
 * Ver la ficha de un registro **no** es entrar a su maestro.
 *
 * Es la distinción que faltaba, y por la que el cajero se quedaba sin poder
 * consultar un dato que necesita para facturar. Entrar al maestro trae la lista
 * completa, el buscador global, la exportación y los botones de alta, edición,
 * baja y sincronización. Ver la ficha es un cajón de sólo lectura del registro
 * que ya está seleccionado.
 *
 * Los dos roles la abren; lo que cambia es qué muestra. Al cajero se le ocultan
 * los campos que sólo sirven para administrar --grupo de precios, descuento del
 * cliente, estado de sincronización-- porque si no, la ficha en contexto sería
 * la puerta trasera del maestro.
 */
export const FICHAS_EN_CONTEXTO = [
  { clave: "clientes", desde: "el selector de cliente de facturación" },
  { clave: "colegios", desde: "el selector de colegio de facturación" },
  { clave: "productos", desde: "el nombre del producto en la línea de la factura" },
];

/**
 * Las pestañas de Configuración, que es la única pantalla que el cajero ve
 * entera pero no completa.
 */
const PESTANAS_DE_CONFIGURACION = {
  1: { nombre: "Configuración general", cajero: true },
  2: { nombre: "Estado de sincronización", cajero: true },
  params: { nombre: "Parámetros regionales" },
  3: { nombre: "Impresión de tickets", cajero: true },
  mantenimiento: { nombre: "Mantenimiento" },
};

/**
 * Lo que un país le quita **además** a su cajero.
 *
 * La matriz de arriba vale para todos los países; esto la ajusta hacia abajo.
 * Sólo puede **quitar, nunca dar**: un país no puede abrirle al cajero algo que
 * la regla general le niega, y el supervisor no se ve afectado nunca. Si mañana
 * un país decide que su cajero tampoco vea Productos, es agregar
 * `"productos"` a su lista y nada más.
 *
 * Hoy está vacío a propósito: todos los países siguen la misma matriz. Perú
 * tenía su propio recorte desde antes de que existiera esta tabla --le quitaba
 * a su cajero consulta de stock, abonos, pendientes e informes-- y se retiró
 * para que no quedara una excepción sin dueño.
 */
const RECORTES_POR_PAIS = {};

/**
 * La matriz ya resuelta para un país, tal como se ve en Configuración.
 *
 * @returns {Array<{clave: string, nombre: string, supervisor: true, cajero: boolean, soloLecturaCajero: boolean}>}
 */
export const matrizDePermisos = (countryCode) => {
  const recorte = RECORTES_POR_PAIS[countryCode] || [];

  return entradas.map(([clave, pantalla]) => ({
    clave,
    nombre: pantalla.nombre,
    grupo: pantalla.grupo,
    // El supervisor ve todo: es la premisa, no una casilla que se pueda cambiar.
    supervisor: true,
    cajero: Boolean(pantalla.cajero) && !recorte.includes(clave),
    soloLecturaCajero: Boolean(pantalla.soloLecturaCajero),
    recortadoPorPais: recorte.includes(clave),
    fichaEnContexto: FICHAS_EN_CONTEXTO.some((ficha) => ficha.clave === clave),
  }));
};

/** Desde dónde se abre la ficha de este maestro, si es que se abre. */
export const fichaEnContextoDe = (clavePantalla) =>
  FICHAS_EN_CONTEXTO.find((ficha) => ficha.clave === clavePantalla) || null;

// ─── Derivados ─────────────────────────────────────────────────────────────

const entradas = Object.entries(PANTALLAS);

/** Un usuario que no es administrador es cajero. Aplica a todos los países. */
export const isCajero = (countryCode, isAdmin) => !isAdmin;

const pantallasVedadas = (countryCode) => {
  const recorte = RECORTES_POR_PAIS[countryCode] || [];
  return entradas.filter(([clave, p]) => !p.cajero || recorte.includes(clave));
};

/**
 * Claves de menú que el cajero no ve.
 *
 * Incluye el padre cuando todos sus hijos caen: sin eso quedaba un submenú que
 * se abre y no tiene nada adentro.
 */
export const getCajeroHiddenMenu = (countryCode) => {
  const vedadas = pantallasVedadas(countryCode)
    .map(([, p]) => p.menu)
    .filter(Boolean);

  const visibles = entradas
    .filter(([clave, p]) => p.cajero && !(RECORTES_POR_PAIS[countryCode] || []).includes(clave))
    .map(([, p]) => p.menu)
    .filter(Boolean);

  const padres = [...new Set(vedadas.map((clave) => clave.split(".")[0]))].filter(
    (padre) => !visibles.some((clave) => clave === padre || clave.startsWith(`${padre}.`))
  );

  return [...new Set([...vedadas, ...padres])];
};

/** Se conserva el nombre anterior para no romper a quien ya lo importaba. */
export const CAJERO_HIDDEN_MENU = getCajeroHiddenMenu();

export const canAccessRoute = (countryCode, isAdmin, path) => {
  if (!isCajero(countryCode, isAdmin)) return true;

  const vedada = pantallasVedadas(countryCode).some(([, p]) => p.ruta === path);
  return !vedada;
};

/**
 * Si esta pantalla le está vedada a este usuario.
 *
 * La usa el tablero de inicio para no ofrecer un atajo a una pantalla a la que
 * después no se puede entrar.
 */
export const pantallaVedadaParaCajero = (clavePantalla, countryCode, isAdmin) =>
  isCajero(countryCode, isAdmin) &&
  pantallasVedadas(countryCode).some(([clave]) => clave === clavePantalla);

/**
 * Si el cajero sólo puede consultar esta pantalla.
 *
 * La usan las listas para esconder editar, eliminar y sincronizar dejando ver.
 */
export const esSoloLecturaParaCajero = (clavePantalla, countryCode, isAdmin) =>
  isCajero(countryCode, isAdmin) && Boolean(PANTALLAS[clavePantalla]?.soloLecturaCajero);

/** Las pestañas de Configuración que corresponden a este usuario. */
export const pestanasDeConfiguracionVisibles = (countryCode, isAdmin) => {
  if (!isCajero(countryCode, isAdmin)) return Object.keys(PESTANAS_DE_CONFIGURACION);

  return Object.entries(PESTANAS_DE_CONFIGURACION)
    .filter(([, pestana]) => pestana.cajero)
    .map(([clave]) => clave);
};

export const getUserRoleLabel = (countryCode, isAdmin) => (isAdmin ? "Supervisor" : "Cajero");

// Se exportan para las pruebas y para la ficha de Configuración, que muestra
// qué alcanza cada rol.
export { PANTALLAS, PESTANAS_DE_CONFIGURACION, RECORTES_POR_PAIS };
